import { useState, useEffect, useCallback } from "react";
import { useSearch } from "wouter";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck, Lock, Tag } from "lucide-react";
import { resolveReferralCode, setReferralCookie } from "@/lib/referralCookie";
import { parseFundable360CheckoutParams } from "@/lib/fundable360CheckoutParams";

const NAVY = "#0E2841";
const TEAL = "#156082";

const PROGRAM_LABELS: Record<string, string> = {
  foundation: "Foundation",
  growth: "Growth",
  elite: "Elite",
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
}

interface InitData {
  clientSecret: string;
  sessionId: string;
  publishableKey: string;
  program: string;
  method: "paid_in_full" | "financed" | "subscription";
  amountCents: number;
  isTest: boolean;
  // Subscription only: the one-time setup fee added to the first invoice.
  setupFeeCents?: number;
}

export default function Fundable360Checkout() {
  const search = useSearch();
  const checkoutParams = parseFundable360CheckoutParams(search);
  const { program, method, error: paramsError } = checkoutParams;
  const urlRef = checkoutParams.ref;
  const ref = resolveReferralCode(urlRef);
  const source = checkoutParams.source;
  const test = checkoutParams.test;

  const [initData, setInitData] = useState<InitData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<
    typeof loadStripe
  > | null>(null);
  const [disclosureAccepted, setDisclosureAccepted] = useState(false);

  useEffect(() => {
    if (urlRef) setReferralCookie(urlRef);
  }, [urlRef]);

  useEffect(() => {
    setInitData(null);
    setStripePromise(null);
    setDisclosureAccepted(method === "paid_in_full");

    if (paramsError || !method) {
      setError(
        paramsError ?? "Unable to determine the selected payment option."
      );
      return;
    }
    setError(null);

    const query = new URLSearchParams({ program, method });
    if (ref) query.set("ref", ref);
    if (source) query.set("source", source);
    if (test) query.set("test", test);

    const controller = new AbortController();
    fetch(`/api/billing/fundable360/checkout/init?${query.toString()}`, {
      signal: controller.signal,
    })
      .then(r => r.json())
      .then((data: InitData & { error?: string }) => {
        if (data.error) {
          setError(data.error);
          return;
        }
        if (data.method !== method) {
          setError(
            "The payment option returned by checkout did not match your selection. Please start again."
          );
          return;
        }
        setInitData(data);
        setStripePromise(loadStripe(data.publishableKey));
      })
      .catch(error => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setError("Failed to start checkout. Please try again.");
      });

    return () => controller.abort();
  }, [program, method, paramsError, ref, source, test]);

  const fetchClientSecret = useCallback(
    () => Promise.resolve(initData?.clientSecret ?? ""),
    [initData?.clientSecret]
  );

  const programLabel = PROGRAM_LABELS[program] ?? program;
  const isFinanced = method === "financed";
  const isSubscription = method === "subscription";
  // Both financing and the ongoing subscription require the client to actively
  // agree before the payment form appears. Paid-in-full has nothing to agree to.
  const needsConsent = isFinanced || isSubscription;
  const paymentOptionLabel = isFinanced
    ? "6-Month Financing"
    : isSubscription
      ? "Monthly Subscription"
      : method === "paid_in_full"
        ? "Paid in Full"
        : "Payment option unavailable";
  const priceDisplay = initData
    ? isFinanced
      ? `${formatCents(initData.amountCents)}/month for 6 months`
      : isSubscription
        ? `${formatCents(initData.amountCents)}/month`
        : `${formatCents(initData.amountCents)} one-time`
    : null;
  const setupFeeCents = initData?.setupFeeCents ?? 0;
  const dueTodayCents = initData ? initData.amountCents + setupFeeCents : 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header style={{ background: NAVY }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white font-serif font-bold text-lg tracking-wide">
              FUNDABLE360
            </span>
            {initData?.isTest && (
              <span className="text-xs bg-blue-500/20 text-blue-300 font-medium px-2 py-0.5 rounded-full border border-blue-500/30">
                Test Mode
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-300">
            <Lock className="w-3.5 h-3.5" />
            Secured by Stripe
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-5xl mx-auto w-full px-4 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: order summary */}
        <aside className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
              Your Program
            </p>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Fundable360 {programLabel}
            </h1>
            <p className="text-sm text-gray-500 mb-4">{paymentOptionLabel}</p>

            {ref && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-xs font-semibold text-green-700 mb-4">
                <Tag className="w-3 h-3" />
                Referral code: <span className="font-mono">{ref}</span>
              </div>
            )}

            {priceDisplay && (
              <div className="border-t border-gray-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {isFinanced || isSubscription ? "Monthly payment" : "Enrollment"}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {priceDisplay}
                  </span>
                </div>
                {/* The subscription charges more on day one than the headline
                    monthly figure, so the setup fee and the real first payment
                    are both spelled out rather than left as a surprise. */}
                {isSubscription && setupFeeCents > 0 && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">One-time setup fee</span>
                      <span className="font-semibold text-gray-900">
                        {formatCents(setupFeeCents)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-gray-100 pt-2">
                      <span className="font-semibold text-gray-900">Due today</span>
                      <span className="font-bold text-gray-900">
                        {formatCents(dueTodayCents)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 pt-1">
                      Then {formatCents(initData!.amountCents)} per month. Cancel anytime.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Subscription authorisation. Mirrors the wording Stripe shows on
              Scale360's subscription checkout ("you authorize us to charge you
              according to the terms until you cancel"), which the client asked
              for explicitly so a subscriber knows they are starting an ongoing
              charge rather than a fixed payment plan. */}
          {isSubscription && (
            <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-3">
                Subscription Terms
              </p>
              <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={disclosureAccepted}
                  onChange={e => setDisclosureAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 shrink-0"
                  style={{ accentColor: TEAL }}
                />
                <span>
                  I authorize Fundable360 to charge{" "}
                  <span className="font-semibold text-gray-900">
                    {formatCents(dueTodayCents)}
                  </span>{" "}
                  today, then{" "}
                  <span className="font-semibold text-gray-900">
                    {formatCents(initData?.amountCents ?? 0)} per month
                  </span>{" "}
                  until I cancel. Missed payments may restrict my account
                  access.
                </span>
              </label>
            </div>
          )}

          {isFinanced && (
            <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2">
                Financing Disclosure
              </p>
              <p className="text-sm text-gray-600 mb-3">
                By checking the box below, I acknowledge that I am enrolling in
                the Fundable360 {programLabel} on a 6-month payment schedule. I
                authorize Fundable360 to charge the payment method I provide{" "}
                {formatCents(initData?.amountCents ?? 0)} per month for 6
                consecutive months, for a total of{" "}
                {initData ? formatCents(initData.amountCents * 6) : "-"}.
              </p>
              <p className="text-sm text-gray-600 mb-3">
                I understand that this is a payment schedule for the Fundable360{" "}
                {programLabel} and is not a loan or extension of credit. If a
                scheduled payment is declined, missed, or otherwise not
                successfully completed, I understand that Fundable360 reserves
                the right to restrict or suspend access to my account, platform,
                services, and program resources until my account is brought
                current.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                I may contact Fundable360 support with questions regarding my
                payment schedule or account status before completing checkout.
              </p>
              <label className="flex items-start gap-2.5 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={disclosureAccepted}
                  onChange={e => setDisclosureAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300"
                  style={{ accentColor: TEAL }}
                />
                <span>
                  I agree to the 6-month payment schedule and understand that
                  missed or failed payments may result in restricted access to
                  my Fundable360 account until my account is brought current.
                </span>
              </label>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>Your payment is encrypted and secure</span>
            </div>
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <ShieldCheck className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>Concierge onboarding included</span>
            </div>
          </div>
        </aside>

        {/* Right: Stripe embedded checkout */}
        <main className="lg:col-span-3">
          {error ? (
            <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center">
              <p className="text-red-600 font-medium mb-2">
                Unable to start checkout
              </p>
              <p className="text-sm text-gray-500">{error}</p>
              <a
                href="https://fundable360.wibiz.ai/"
                className="mt-4 inline-block text-sm font-medium hover:underline"
                style={{ color: TEAL }}
              >
                ← Back to Fundable360
              </a>
            </div>
          ) : !initData || !stripePromise ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
              <p className="text-sm text-gray-500">Preparing your checkout…</p>
            </div>
          ) : needsConsent && !disclosureAccepted ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 flex flex-col items-center justify-center gap-3 text-center">
              <ShieldCheck className="w-8 h-8 text-gray-300" />
              <p className="text-sm text-gray-500 max-w-xs">
                Please review and accept the terms on the left before
                continuing to payment.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </main>
      </div>

      <footer className="py-4 text-center text-xs text-gray-400">
        © {new Date().getFullYear()} Fundable360 · Powered by{" "}
        <a
          href="https://stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          Stripe
        </a>
      </footer>
    </div>
  );
}
